import React, { useState } from 'react';
import { Plus, Briefcase } from 'lucide-react';
import { useCreateService, useDeactivateService, useDeleteService, useServices, useUpdateService } from '../../hooks/useBookingCatalog';
import ServiceTable from '../../components/booking/ServiceTable';
import ServiceModal from '../../components/booking/ServiceModal';
import { BookingServiceResponseDto, MeetingType } from '../../types/booking';
import { ServiceFormValues } from '../../utils/bookingValidators';

export default function ServicesPage() {
  const { data: services, isLoading } = useServices();
  const createService = useCreateService();
  const updateService = useUpdateService();
  const deleteService = useDeleteService();
  const deactivateService = useDeactivateService();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<BookingServiceResponseDto | null>(null);

  const handleCreate = () => {
    setEditingService(null);
    setModalOpen(true);
  };

  const handleEdit = (service: BookingServiceResponseDto) => {
    setEditingService(service);
    setModalOpen(true);
  };

  const handleSave = (values: ServiceFormValues) => {
    const payload = {
      name: values.name,
      description: values.description,
      durationMinutes: Number(values.durationMinutes),

      price:
        values.price === '' ||
        values.price === null
          ? null
          : Number(values.price),

      meetingType: values.meetingType,

      meetingLocation:
        values.meetingType === MeetingType.OnSite
          ? values.meetingLocation.trim()
          : null,

      active: values.active,
      sortOrder: Number(values.sortOrder),
    };

    const onSuccess = () => setModalOpen(false);

    if (editingService) {
      updateService.mutate({ id: editingService.id, data: payload }, { onSuccess });
    } else {
      createService.mutate(payload, { onSuccess });
    }
  };

  const handleDelete = (service: BookingServiceResponseDto) => {
    if (window.confirm(`Delete service "${service.name}"? This cannot be undone.`)) {
      deleteService.mutate(service.id);
    }
  };

  const handleDeactivate = (service: BookingServiceResponseDto) => {
    deactivateService.mutate({
      id: service.id,
      data: {
        name: service.name,
        description: service.description ?? undefined,
        durationMinutes: service.durationMinutes,
        price: service.price,
        active: false,
        sortOrder: service.sortOrder,
        meetingType: service.meetingType,
        meetingLocation: service.meetingLocation
      },
    });
  };

  return (
    <div className="space-y-6 text-left">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2"><Briefcase size={22} /> Service Management</h1>
          <p className="text-sm text-strivre-mutedtext mt-1">Create and maintain the services customers can book.</p>
        </div>
        <button onClick={handleCreate} className="flex items-center gap-2 bg-strivre-navy text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-strivre-navy/90">
          <Plus size={18} /> Create Service
        </button>
      </div>

      <ServiceTable
        services={services ?? []}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDeactivate={handleDeactivate}
      />

      <ServiceModal
        isOpen={modalOpen}
        service={editingService}
        saving={createService.isPending || updateService.isPending}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}
